import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { PlatformCall } from './common';
import { fmtDate } from './common';

/**
 * How many employees one workspace may hold, and the platform-wide free
 * allowance it inherits when nobody has said otherwise.
 *
 * Two surfaces, both here:
 *   * `WorkspaceLimits` — a tab inside the workspace dialog.
 *   * `LimitsSettingsTab` — the fleet-wide policy, under the main tabs.
 *
 * The number is only half of it: a limit that is not enforced still shows the
 * seat count, which is what an operator actually wants most of the time
 * ("who is about to outgrow what they pay for?"). Enforcement is the master
 * switch on the settings tab and is off until someone turns it on.
 */

export type LimitMode = 'DEFAULT' | 'CUSTOM' | 'UNLIMITED';

export interface TenantLimits {
  tenant_key: string;
  employee_mode: LimitMode;
  employee_limit: number | null;
  note: string;
  updated_by: string | null;
  updated_at: string | null;
  effective_limit: number | null;
  free_employee_limit: number;
  used: number | null;
  remaining: number | null;
  over: boolean;
  enforced: boolean;
  error: string | null;
}

export interface LimitsPolicy {
  enforce: boolean;
  free_employee_limit: number;
  upgrade_contact: string;
}

/** "12 / 25", "12 / unlimited", "— / 25" when the workspace is unreachable. */
export function seatLabel(used: number | null, limit: number | null): string {
  return `${used ?? '—'} / ${limit === null ? '∞' : limit}`;
}

export function WorkspaceLimits({
  tenantKey,
  call,
  onChanged,
}: {
  tenantKey: string;
  call: PlatformCall;
  onChanged?: () => void;
}) {
  const [data, setData] = useState<TenantLimits | null>(null);
  const [mode, setMode] = useState<LimitMode>('DEFAULT');
  const [limit, setLimit] = useState('');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const apply = (body: TenantLimits) => {
    setData(body);
    setMode(body.employee_mode);
    setLimit(body.employee_limit === null ? '' : String(body.employee_limit));
    setNote(body.note ?? '');
  };

  const load = useCallback(async () => {
    setErr(null);
    try {
      apply((await call(`/tenants/${tenantKey}/limits`)) as TenantLimits);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [tenantKey, call]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      const body = (await call(`/tenants/${tenantKey}/limits`, {
        method: 'PUT',
        body: JSON.stringify({
          employee_mode: mode,
          // Only CUSTOM carries a number; the server ignores it otherwise.
          employee_limit: mode === 'CUSTOM' && limit.trim() !== '' ? Number(limit) : null,
          note: note.trim(),
        }),
      })) as TenantLimits;
      apply(body);
      setSaved(true);
      onChanged?.();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!data && !err) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const used = data?.used ?? null;
  const effective = data?.effective_limit ?? null;
  const pct =
    used !== null && effective !== null && effective > 0
      ? Math.min(100, Math.round((used / effective) * 100))
      : 0;
  const dirty =
    !!data &&
    (mode !== data.employee_mode ||
      note.trim() !== (data.note ?? '') ||
      (mode === 'CUSTOM' &&
        limit.trim() !== (data.employee_limit === null ? '' : String(data.employee_limit))));

  return (
    <Box>
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {data && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
            <Typography variant="h5">{seatLabel(used, effective)}</Typography>
            <Typography variant="body2" color="text.secondary">
              employees in use
            </Typography>
          </Stack>
          {effective !== null && (
            <LinearProgress
              variant="determinate"
              value={pct}
              color={data.over ? 'error' : pct >= 90 ? 'warning' : 'primary'}
              sx={{ height: 8, borderRadius: 1, my: 1 }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            Counts people this workspace still employs. Anyone resigned or terminated keeps
            their payroll history but does not take a seat.
          </Typography>
          {data.error && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Could not count employees in this workspace: {data.error}
            </Alert>
          )}
          {data.over && (
            <Alert severity="error" sx={{ mt: 1 }}>
              This workspace is over its limit by {(used ?? 0) - (effective ?? 0)} employee(s).
              {data.enforced
                ? ' Nobody new can be added until the limit is raised.'
                : ' Nothing is being refused. Enforcement is off fleet-wide.'}
            </Alert>
          )}
          {!data.enforced && !data.over && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Enforcement is off fleet-wide, so this limit is reported but not applied. Turn it
              on under Limits in the main tabs.
            </Alert>
          )}
        </Paper>
      )}

      <Typography variant="subtitle2" gutterBottom>
        Employee allowance
      </Typography>
      <RadioGroup value={mode} onChange={(e) => setMode(e.target.value as LimitMode)}>
        <FormControlLabel
          value="DEFAULT"
          control={<Radio size="small" />}
          label={`Platform default: ${data?.free_employee_limit ?? 0} employees (free tier)`}
        />
        <FormControlLabel
          value="CUSTOM"
          control={<Radio size="small" />}
          label="A number for this workspace only. They paid for more"
        />
        <FormControlLabel
          value="UNLIMITED"
          control={<Radio size="small" />}
          label="Unlimited: no cap on this workspace"
        />
      </RadioGroup>

      {mode === 'CUSTOM' && (
        <TextField
          label="Employees allowed"
          size="small"
          type="number"
          sx={{ mt: 1, width: 220 }}
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          inputProps={{ min: 0, max: 1000000 }}
          helperText={
            used !== null && limit.trim() !== '' && Number(limit) < used
              ? `This workspace already has ${used}. They keep working; nobody new can be added.`
              : 'What this client has paid for'
          }
        />
      )}

      <TextField
        label="Note"
        size="small"
        fullWidth
        sx={{ mt: 2 }}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. 150 seats, PO 4471, renewed Apr 2027"
        helperText="Why this number. Shown here and recorded in the audit trail."
      />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Button variant="contained" disabled={busy || !dirty} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {saved && !dirty && (
          <Typography variant="body2" color="success.main">
            Saved. It applies to the next employee they add.
          </Typography>
        )}
      </Stack>

      {data?.updated_at && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Last changed {fmtDate(data.updated_at)}
            {data.updated_by ? ` by ${data.updated_by}` : ''}
          </Typography>
        </>
      )}
    </Box>
  );
}

export function LimitsSettingsTab({ call }: { call: PlatformCall }) {
  const [policy, setPolicy] = useState<LimitsPolicy | null>(null);
  const [draft, setDraft] = useState<LimitsPolicy | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const body = (await call('/settings/limits')) as { settings: LimitsPolicy };
      setPolicy(body.settings);
      setDraft(body.settings);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [call]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setErr(null);
    setSaved(false);
    try {
      const body = (await call('/settings/limits', {
        method: 'PUT',
        body: JSON.stringify({
          enforce: draft.enforce,
          free_employee_limit: Number(draft.free_employee_limit) || 0,
          upgrade_contact: draft.upgrade_contact,
        }),
      })) as { settings: LimitsPolicy };
      setPolicy(body.settings);
      setDraft(body.settings);
      setSaved(true);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!draft) {
    return err ? (
      <Alert severity="error">{err}</Alert>
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(policy);

  return (
    <Box sx={{ maxWidth: 720 }}>
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={draft.enforce}
              onChange={(e) => setDraft({ ...draft, enforce: e.target.checked })}
            />
          }
          label="Enforce employee limits"
        />
        <Typography variant="body2" color="text.secondary">
          Off: seat counts are shown here but nothing is ever refused. On: a workspace that has
          reached its allowance cannot add employees: not from the Employees page, not from a
          roster import, and not from the uKnowva sync. Payroll for the people already in the
          workspace is never affected, and nobody is ever signed out by a limit.
        </Typography>
      </Paper>

      <TextField
        label="Free employees per workspace"
        size="small"
        type="number"
        sx={{ width: 260 }}
        value={draft.free_employee_limit}
        onChange={(e) =>
          setDraft({ ...draft, free_employee_limit: Number(e.target.value) })
        }
        inputProps={{ min: 0, max: 1000000 }}
        helperText="Every workspace left on 'platform default' inherits this"
      />

      <TextField
        label="Who to contact to buy more"
        size="small"
        fullWidth
        sx={{ mt: 2 }}
        value={draft.upgrade_contact}
        onChange={(e) => setDraft({ ...draft, upgrade_contact: e.target.value })}
        placeholder="e.g. Email billing@yourcompany.com to add seats."
        helperText="Appended to the message a workspace sees when it runs out"
      />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3 }}>
        <Button variant="contained" disabled={busy || !dirty} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save'}
        </Button>
        {saved && !dirty && (
          <Typography variant="body2" color="success.main">
            Saved.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
